import {Pipe, PipeTransform} from "@angular/core";

import {UtilsService} from "framework/services/utils.service";
import moment from "framework/utils/moment";

@Pipe({
    name: "o3ShortDate",
    standalone: false
})
export class ShortDateFormatPipe implements PipeTransform
{
    transform(value: Date | moment.Moment | string | number): string
    {
        return ShortDateFormatPipe.format(value);
    }

    public static format(value: Date | moment.Moment | string | number): string
    {
        if (!value) return "";
        return MomentHelper.parse(value)
                           .format("l");
    }
}

@Pipe({
    name: "o3ShortTime",
    standalone: false
})
export class ShortTimeFormatPipe implements PipeTransform
{
    transform(value: Date | moment.Moment | string | number,
              ...args: any[]): string
    {
        if (!value) return "";
        return MomentHelper.parse(value)
                           .format("LT");
    }
}

@Pipe({
    name: "csRelativeTime",
    standalone: false
})
export class RelativeTimePipe implements PipeTransform
{
    transform(value: Date | moment.Moment | string | number): string
    {
        return RelativeTimePipe.format(value);
    }

    public static format(value: Date | moment.Moment | string | number): string
    {
        if (!value) return "";

        return MomentHelper.parse(value, "local")
                           .from(MomentHelper.now());
    }
}

@Pipe({
    name: "csDateTime",
    standalone: false
})
export class DateTimeFormatPipe implements PipeTransform
{
    transform(value: Date | moment.Moment | string | number,
              ...args: boolean[]): string
    {
        return DateTimeFormatPipe.format(value, ...args);
    }

    public static format(value: Date | moment.Moment | string | number,
                         ...args: boolean[]): string
    {
        if (!value) return "";

        let text = MomentHelper.friendlyFormatConciseUS(value, true);

        const includeRelative = args[0];
        if (includeRelative)
        {
            text = `${text} (${RelativeTimePipe.format(value)})`;
        }

        return text;
    }
}

@Pipe({
    name: "o3Time",
    standalone: false
})
export class TimeFormatPipe implements PipeTransform
{
    transform(value: Date | moment.Moment | string | number,
              ...args: any[]): string
    {
        if (!value) return "";
        return MomentHelper.parse(value)
                           .format("LTS");
    }
}

@Pipe({
    name: "o3LongDate",
    standalone: false
})
export class LongDateFormatPipe implements PipeTransform
{
    transform(value: Date | moment.Moment | string | number,
              ...args: any[]): string
    {
        if (!value) return "";
        return MomentHelper.parse(value)
                           .format("ll");
    }
}

@Pipe({
    name: "o3LongDateTime",
    standalone: false
})
export class LongDateTimeFormatPipe implements PipeTransform
{
    transform(value: Date | moment.Moment | string | number,
              ...args: any[]): string
    {
        if (!value) return "";
        return MomentHelper.parse(value)
                           .format("L LTS");
    }
}


@Pipe({
    name: "o3Elapsed",
    standalone: false
})
export class ElapsedFormatPipe implements PipeTransform
{
    transform(value: Date,
              start: Date): string
    {
        if (!value) return "";

        let moment = MomentHelper.parse(value);
        return moment.from(start, true);
    }
}

export class MomentHelper
{
    public static readonly formatGranularityDaySlash = "M/D/YYYY";
    public static readonly formatGranularityDay      = "D MMM YY";
    public static readonly formatGranularityMonth    = "MMM YY";
    public static readonly formatGranularityYear     = "YYYY";
    public static readonly formatGranularityWeek     = "[Week ]WW 'GG";
    public static readonly formatGranularityQuarter  = "[Q]Q 'YY";
    public static readonly ISO_WEEK                  = "isoWeek";

    public static readonly MS_IN_DAY: number = 24 * 60 * 60 * 1_000;

    public static now(): moment.Moment
    {
        return MomentHelper.parse(moment.now());
    }

    public static nowWithLocalZone(): moment.Moment
    {
        return MomentHelper.toZone(MomentHelper.now(), MomentHelper.getLocalZone());
    }

    public static nowWithUTC(): moment.Moment
    {
        return MomentHelper.parse(moment.now())
                           .utc();
    }

    public static compareDates(a: Date | moment.Moment,
                               b: Date | moment.Moment,
                               ascending: boolean,
                               nullToEnd?: boolean): number
    {
        let timestampA = MomentHelper.parseOrNull(a)
                                     ?.valueOf() || NaN;
        let timestampB = MomentHelper.parseOrNull(b)
                                     ?.valueOf() || NaN;

        return UtilsService.compareNumbers(timestampA, timestampB, ascending, nullToEnd);
    }

    public static min(a: moment.Moment,
                      b: moment.Moment): moment.Moment
    {
        if (!a) return b;
        if (!b) return a;

        return a.isSameOrBefore(b) ? a : b;
    }

    public static max(a: moment.Moment,
                      b: moment.Moment): moment.Moment
    {
        if (!a) return b;
        if (!b) return a;

        return a.isSameOrAfter(b) ? a : b;
    }

    public static startOf(moment: moment.Moment,
                          unitOfTime: moment.unitOfTime.StartOf): moment.Moment
    {
        return moment.clone()
                     .startOf(unitOfTime);
    }

    public static endOf(moment: moment.Moment,
                        unitOfTime: moment.unitOfTime.StartOf): moment.Moment
    {
        return moment.clone()
                     .endOf(unitOfTime);
    }

    public static getDuration(amount: moment.DurationInputArg1,
                              duration: moment.unitOfTime.DurationConstructor,
                              unit: moment.unitOfTime.Base = "millisecond"): number
    {
        return moment.duration(amount, duration)
                     .as(unit);
    }

    public static atBeginningOfDay(moment: moment.Moment): boolean
    {
        return moment.isSame(moment.clone()
                                   .startOf(TimeUnit.Day));
    }

    public static inSameDay(...times: moment.Moment[]): boolean
    {
        if (times.length < 2)
        {
            return true;
        }

        const start = times[0].startOf(TimeUnit.Day);
        for (let i = 1; i < times.length; i++)
        {
            if (!times[i].startOf(TimeUnit.Day)
                         .isSame(start))
            {
                return false;
            }
        }
        return true;
    }

    public static add(moment: moment.Moment,
                      amount?: moment.DurationInputArg1,
                      unit?: moment.unitOfTime.DurationConstructor): moment.Moment
    {
        return moment.clone()
                     .add(amount, unit);
    }

    public static subtract(moment: moment.Moment,
                           amount?: moment.DurationInputArg1,
                           unit?: moment.unitOfTime.DurationConstructor): moment.Moment
    {
        return moment.clone()
                     .subtract(amount, unit);
    }

    public static parse(value: Date | moment.Moment | string | number,
                        zone?: string): moment.Moment
    {
        // under systemjs, moment is actually exported as the default export, so we account for that
        const momentConstructor: (value?: any) => moment.Moment = (<any>moment).default || moment;

        let result = momentConstructor(value);
        return MomentHelper.toZone(result, zone);
    }

    // With a format, parses directly in UTC mode (moment.utc) instead of the default fallback
    // parser, which reads a non-ISO string in the local zone before relabeling it UTC.
    public static parseAsUTC(value: Date | moment.Moment | string | number,
                             format?: string): moment.Moment
    {
        return format ? moment.utc(value, format) : MomentHelper.parse(value, "UTC");
    }

    public static parseOrNull(value: Date | moment.Moment | string | number | undefined | null,
                              zone?: string): moment.Moment
    {
        if (!value) return null;

        return MomentHelper.parse(value, zone);
    }

    public static parseAsDate(value: Date | moment.Moment | string | number): Date
    {
        return MomentHelper.parse(value)
                           .toDate();
    }

    public static toLocalDate(value: moment.Moment,
                              keepLocalTime = true): Date
    {
        return value.clone()
                    .local(keepLocalTime)
                    .toDate();
    }

    public static fileNameFormat(value: moment.Moment = MomentHelper.now()): string
    {
        return value.format("YYYYMMDD_HHmmss");
    }

    public static dateUS(value: Date | moment.Moment | string | number,
                         alwaysShowYear: boolean): string
    {
        value = MomentHelper.parseOrNull(value || MomentHelper.now());

        let format = "M/D";
        if (alwaysShowYear || value.year() !== new Date().getFullYear()) format += "/YY";

        return value.format(format);
    }

    public static friendlyFormatConciseUS(value: moment.Moment | number | Date | string,
                                          alwaysShowYear: boolean): string
    {
        value = MomentHelper.parse(value);

        let start = "M/D";
        let end   = " [at] h:mm A";
        if (alwaysShowYear || value.year() !== new Date().getFullYear())
        {
            start += "/YY";
        }

        return value.format(start + end);
    }

    public static friendlyFormatVerboseUS(value: moment.Moment,
                                          withDay: boolean = true,
                                          withMs: boolean  = false): string
    {
        withMs = withMs ?? value.valueOf() % 10 != 0;

        let formatString = "ddd MMM ";
        if (withDay)
        {
            formatString += "D ";
        }

        if (value.year() !== new Date().getFullYear())
        {
            formatString += "Y ";
        }

        formatString += withMs ? "h:mm:ss.SSS a" : "h:mm:ss a";

        return value.format(formatString);
    }

    public static asUtc(value: moment.Moment | number | Date | string): Date
    {
        return MomentHelper.parse(value)
                           .utc()
                           .toDate();
    }

    public static utcOffset(zone: moment.MomentZone | string = moment.tz.guess(),
                            timestamp: number                = new Date().valueOf()): number
    {
        let zone2 = typeof zone === "string" ? MomentHelper.timeZone(zone) : zone;
        return zone2.utcOffset(timestamp);
    }

    public static toZone(value: moment.Moment,
                         zoneName: string,
                         keepLocalTime: boolean = false)
    {
        if (!value || !zoneName) return value;

        if (zoneName == "local")
        {
            zoneName = MomentHelper.getLocalZone();
        }

        return value.tz(zoneName, keepLocalTime);
    }

    public static toDateWithZone(zoneName: string): Date
    {
        // Parse the input to a moment object with the specified timezone
        const momentInZone = moment.tz(moment(), zoneName);

        // Get the date parts and explicitly construct a Date object
        const year        = momentInZone.year();
        const month       = momentInZone.month(); // Note: moment months are zero-based
        const day         = momentInZone.date();
        const hour        = momentInZone.hour();
        const minute      = momentInZone.minute();
        const second      = momentInZone.second();
        const millisecond = momentInZone.millisecond();

        // Create a new Date object with the same time components
        return new Date(year, month, day, hour, minute, second, millisecond);
    }

    public static convertToUTCFromTimezone(value: Date,
                                           userPreferredTimezone: string): Date
    {
        const browserMoment                 = moment(value);
        const userMomentInPreferredTimezone = moment.tz(browserMoment.format("YYYY-MM-DDTHH:mm:ss"), userPreferredTimezone);
        const utcMoment                     = userMomentInPreferredTimezone.clone()
                                                                           .utc();

        return new Date(
            Date.UTC(
                utcMoment.year(),
                utcMoment.month(),
                utcMoment.date(),
                utcMoment.hour(),
                utcMoment.minute(),
                utcMoment.second(),
                utcMoment.millisecond()
            )
        );
    }

    public static formatToLocalDateString(date: any,
                                          format: string): string
    {
        const localZone = MomentHelper.getLocalZone();
        return MomentHelper.parse(date)
                           .tz(localZone)
                           .format(format);
    }

    public static getZoneAbbreviation(zoneName: string): string
    {
        // Ensure the value is in the desired timezone
        const momentInZone = moment.tz(zoneName);

        // Format the abbreviation using z (e.g., IST, PST)
        return momentInZone.format("z");
    }

    public static timeZone(zoneName: string): moment.MomentZone
    {
        if (!zoneName || zoneName == "local")
        {
            zoneName = MomentHelper.getLocalZone();
        }

        return moment.tz.zone(zoneName);
    }

    public static getZoneNames(): string[]
    {
        return moment.tz.names();
    }

    public static getLocalZone(): string
    {
        return moment.tz.guess();
    }

    public static isValidDate(value: moment.Moment | number | Date | string): boolean
    {
        if (typeof value === "string")
        {
            return moment(value, moment.ISO_8601, true)
                .isValid();
        }

        return moment(value)
            .isValid();
    }

    public static getTimeByGranularity(date: Date,
                                       granularity: moment.unitOfTime.StartOf): string | null
    {
        if (!date || !granularity)
        {
            return null;
        }

        switch (granularity)
        {
            case "day":
                return moment(date)
                    .format(MomentHelper.formatGranularityDaySlash);
            
            case "week":
                return moment(date)
                    .format(MomentHelper.formatGranularityWeek);

            case "month":
                return moment(date)
                    .format(MomentHelper.formatGranularityMonth);
            
            case "quarter":
                return moment(date)
                    .format(MomentHelper.formatGranularityQuarter);

            case "year":
                return moment(date)
                    .format(MomentHelper.formatGranularityYear);

            default:
                return null;
        }
    }
}

export enum TimeUnit
{
    Month       = "month",
    Quarter     = "quarter",
    Week        = "week",
    Year        = "year",
    Day         = "day",
    Millisecond = "millisecond"
}
